/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import isEqual from 'lodash/isEqual';
import { createEarlyFetchCache } from '@jira/polaris__lib-early-fetch-cache/src/createEarlyFetchCache.tsx';
import { fetchData } from '@jira/polaris__lib-early-fetch-cache/src/fetchData.tsx';
import { waitForGlobalVariable } from '@jira/polaris__lib-early-fetch-cache/src/waitForGlobalVariable.tsx';
import { getEarlyScriptObservabilityCache } from '@jira/polaris__lib-early-fetch-cache/src/getEarlyScriptObservabilityCache.tsx';
import { getProjectIdFromMap } from '@jira/polaris__lib-early-fetch-cache/src/getProjectIdFromMap.tsx';
import { getProjectKeyFromLocation } from '@jira/polaris__lib-early-fetch-cache/src/getProjectKeyFromLocation.tsx';
import type { IssueTypesForProjectsResponse } from './IssueTypesForProjectsResponse.types.tsx';
import { getIssueTypesForProjectsUrl } from './getIssueTypesForProjectsUrl.tsx';

type ProjectId = string;

// Create a type-specific early fetch cache for issue types
const earlyFetchCache = createEarlyFetchCache<IssueTypesForProjectsResponse, ProjectId[]>({
	paramsEqualityFn: (a, b) => isEqual(a.map(String), b.map(String)),
});

/**
 * Gets the cached response and marks it as accessed
 * The cache will be automatically cleared after access or if stale
 */
export const getEarlyFetchResponse = (
	projectIds: ProjectId[],
): Promise<IssueTypesForProjectsResponse> | null => {
	return earlyFetchCache.getCachedResponse(projectIds);
};

/**
 * Helper function to fetch issue types with proper type
 */
const fetchIssueTypesForProjects = (
	projectIds: ProjectId[],
): Promise<IssueTypesForProjectsResponse> => {
	if (!projectIds.length) {
		throw new Error('No project ids provided');
	}

	const url = getIssueTypesForProjectsUrl(projectIds);

	return fetchData({ url, method: 'GET' });
};

/**
 * Fetches issue types data early based on the project IDs
 */
export const putIssueTypesForProjectsEarlyScriptResponse = (projectIds: ProjectId[]): void => {
	// Execute the actual fetch for issue types
	const response = fetchIssueTypesForProjects(projectIds);

	// Store the fetch promise in the cache
	earlyFetchCache.setCacheResponse(response, projectIds);
};

/**
 * Starts the early API request for issue types.
 * This function is executed before the main app loads.
 */
export function runGetIssueTypesForProjectsEarlyScript(location: Location): boolean {
	// Parse the location to extract the necessary parameters
	const projectKey = getProjectKeyFromLocation(location);
	if (!projectKey) {
		return false;
	}

	// Use cached projectId if available, otherwise wait for SPA_STATE
	getProjectIdFromMap(projectKey, waitForGlobalVariable)
		.then((projectId) => {
			if (projectId) {
				// For now, we fetch issue types for the current project
				// This can be extended to handle multiple projects if needed
				putIssueTypesForProjectsEarlyScriptResponse([String(projectId)]);
			}
		})
		.catch((error) => {
			getEarlyScriptObservabilityCache()?.recordError(
				'polaris.early-fetch-cache.project-id-mapping',
				'Error fetching issue types',
				error instanceof Error ? error : new Error(String(error)),
			);
		});

	return true;
}
